import type { ComponentProps } from 'react';

import { Box } from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useColorMode } from '#/components/ui/color-mode';

type MonacoStandaloneEditor = Parameters<
  NonNullable<ComponentProps<typeof Editor>['onMount']>
>[0];

const MIN_CONTENT_HEIGHT_PX = 96;
const MAX_CONTENT_HEIGHT_PX = 1_000_000;

interface ProcessorFileCodeReadonlyProps {
  code: string;
}

export function ProcessorFileCodeReadonly({
  code,
}: ProcessorFileCodeReadonlyProps) {
  const { colorMode } = useColorMode();
  const monacoTheme = colorMode === 'dark' ? 'vs-dark' : 'vs-light';
  const editorRef = useRef<MonacoStandaloneEditor | null>(null);
  const [fitHeightPx, setFitHeightPx] = useState(MIN_CONTENT_HEIGHT_PX);

  const clampContentHeight = useMemo(
    () => (raw: number) =>
      Math.max(MIN_CONTENT_HEIGHT_PX, Math.min(raw, MAX_CONTENT_HEIGHT_PX)),
    [],
  );

  useEffect(() => {
    const ed = editorRef.current;
    if (!ed) return;
    ed.layout();
    setFitHeightPx(clampContentHeight(ed.getContentHeight()));
  }, [code, clampContentHeight]);

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      borderColor="border"
      overflow="hidden"
    >
      <Editor
        height={`${fitHeightPx}px`}
        language="javascript"
        theme={monacoTheme}
        value={code}
        onMount={(editor) => {
          editorRef.current = editor;
          setFitHeightPx(clampContentHeight(editor.getContentHeight()));
          editor.onDidContentSizeChange(() => {
            setFitHeightPx(clampContentHeight(editor.getContentHeight()));
          });
        }}
        options={{
          readOnly: true,
          minimap: { enabled: false },
          wordWrap: 'on',
          wrappingStrategy: 'advanced',
          stickyScroll: { enabled: false },
          scrollBeyondLastLine: false,
          folding: true,
          lineNumbers: 'on',
          renderLineHighlight: 'none',
          scrollbar: {
            handleMouseWheel: false,
            alwaysConsumeMouseWheel: false,
          },
        }}
      />
    </Box>
  );
}
